import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import MagicLinkLogin from "./components/MagicLinkLogin";
import GoogleLoginButton from "./components/GoogleLoginButton";

const FEATURES = [
  {
    icon: null,
    title: "Browse Recipes",
    desc: "Filter by mood, meal type, category, strain, effect, and terpene profile to find exactly what you need.",
  },
  {
    icon: null,
    title: "Pantry Match",
    desc: "Tell us what you have on hand and we'll surface recipes you can actually make right now.",
  },
  {
    icon: "❤️",
    title: "My Stash",
    desc: "Heart any recipe to save it. Your personal stash is always one tap away.",
  },
  {
    icon: null,
    title: "Serving Scaler",
    desc: "Scale any recipe to 1×, 2×, or 4× servings with automatic ingredient math.",
  },
  {
    icon: null,
    title: "Custom Recipes",
    desc: "Create, edit, and store your own infused recipes alongside the curated library. Premium.",
  },
  {
    icon: null,
    title: "Dosing Calculator",
    desc: "Dial in your perfect dose with precision. Know exactly how much is in every bite. Premium.",
  },
];

const PAYPAL_BUTTON_ID = "5TYWQPR2L9T58";
const PAYPAL_SDK_SRC =
  "https://www.paypal.com/sdk/js?client-id=BAA0hbkZdt0wkyh4kg998s2mAciH1Mdic4np0TJ4OnTdR6d9ANTe1W_wOT7Bcjg9BEg1A3stoMEL-AKryM&components=hosted-buttons&enable-funding=venmo&currency=USD";

export default function Landing() {
  const navigate = useNavigate();

  useEffect(() => {
    const storedEmail = localStorage.getItem("buddee-userEmail");
    if (storedEmail) navigate("/app");
  }, [navigate]);

  useEffect(() => {
    const containerId = `paypal-container-${PAYPAL_BUTTON_ID}`;

    const renderButton = () => {
      const container = document.getElementById(containerId);
      if (!container || container.children.length > 0) return;
      window.paypal.HostedButtons({ hostedButtonId: PAYPAL_BUTTON_ID }).render(`#${containerId}`);
    };

    if (window.paypal) {
      renderButton();
    } else {
      const scriptId = "paypal-sdk";
      let script = document.getElementById(scriptId);
      if (!script) {
        script = document.createElement("script");
        script.id = scriptId;
        script.src = PAYPAL_SDK_SRC;
        script.crossOrigin = "anonymous";
        document.head.appendChild(script);
      }
      script.addEventListener("load", renderButton);
      return () => script.removeEventListener("load", renderButton);
    }
  }, []);

  return (
    <div className="min-h-screen font-nunito flex flex-col transition-colors duration-300" style={{ backgroundColor: 'var(--surface-bg)' }}>

      {/* Hero */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-16 text-center">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col items-center"
        >
          <img
            src={`${process.env.PUBLIC_URL}/chef-buddee.png`}
            alt="Chef Buddee mascot"
            className="h-56 w-auto mb-4 select-none drop-shadow-md"
          />
          <h1 className="text-5xl sm:text-6xl font-extrabold text-sage-900 dark:text-zinc-100 mb-2">
            Chef Buddee
          </h1>
          <p className="text-sm text-sage-900/50 dark:text-zinc-500 italic font-medium mb-4">
            by Puff Puff Peace
          </p>
          <p className="text-base sm:text-lg text-sage-900/70 dark:text-zinc-300 max-w-md mx-auto mb-3 leading-relaxed">
            The cannabis cooking companion built for real home chefs. Browse
            curated infused recipes, match strains to effects, scale servings,
            save favorites, and cook with total confidence — whether you're a
            first-timer or a seasoned budding chef.
          </p>
          <p className="text-sm text-sage-900/50 dark:text-zinc-500 max-w-sm mx-auto mb-10 leading-relaxed">
            Free to explore. One-time upgrade for the full kitchen.
          </p>

          {/* Auth */}
          <div className="flex flex-col items-center gap-4 w-full max-w-sm">
            <GoogleLoginButton onLogin={(email) => {
              localStorage.setItem('buddee-userEmail', email);
              navigate('/app');
            }} />
            <div className="flex items-center gap-3 w-full">
              <div className="flex-1 h-px bg-sage-900/20 dark:bg-zinc-700" />
              <span className="text-xs text-sage-900/40 dark:text-zinc-500 font-medium">or</span>
              <div className="flex-1 h-px bg-sage-900/20 dark:bg-zinc-700" />
            </div>
            <MagicLinkLogin />
            <p className="text-xs text-sage-900/40 dark:text-zinc-500 mt-1">
              Like what we're cooking?{" "}
              <a
                href="https://www.paypal.com/ncp/payment/5TYWQPR2L9T58"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2 hover:text-sage-900/70 dark:hover:text-zinc-300 transition"
              >
                Support the kitchen 💚
              </a>
            </p>
          </div>
        </motion.div>

        {/* Feature cards — 2 col mobile, 3 col desktop */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.45 }}
          className="mt-14 grid grid-cols-2 sm:grid-cols-3 gap-4 max-w-2xl w-full"
        >
          {FEATURES.map((f, i) => (
            <div
              key={i}
              className="bg-white dark:bg-zinc-900 border border-sage-900/20 dark:border-zinc-700 rounded-2xl p-5 text-center shadow-sm"
            >
              {f.icon && <div className="text-3xl mb-2">{f.icon}</div>}
              <p className="font-bold text-sage-900 dark:text-zinc-100 text-sm">{f.title}</p>
              <p className="text-xs text-sage-900/60 dark:text-zinc-400 mt-1 leading-snug">{f.desc}</p>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Donate */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.55, duration: 0.4 }}
        className="py-10 px-6 flex flex-col items-center text-center border-t border-sage-900/10 dark:border-zinc-800"
      >
        <p className="text-xl font-bold text-sage-900 dark:text-zinc-100 mb-1">Support the Kitchen 🍃</p>
        <p className="text-sm text-sage-900/60 dark:text-zinc-400 max-w-xs mb-5 leading-relaxed">
          Chef Buddee is an indie project from Puff Puff Peace LLC. If it's
          brought some good vibes to your table, a small donation keeps the
          burners on.
        </p>
        <div id={`paypal-container-${PAYPAL_BUTTON_ID}`} className="min-w-[200px]" />
        <p className="text-xs text-sage-900/40 dark:text-zinc-600 mt-3 italic">
          Every bit helps us grow — thank you!
        </p>
      </motion.div>

      {/* Footer */}
      <footer className="py-5 text-center text-xs text-sage-900/40 dark:text-zinc-600">
        © {new Date().getFullYear()} Puff Puff Peace LLC — elevated flavors, literally.
      </footer>
    </div>
  );
}
